<template>
  <VDialogV3
      position="right"
      :title="dialogTitle"
      :loading="false"
      width="1200px"
      @submit="onSubmit"
  >
    <VDataGridV2
        id="payment-request-positions"
        :loader="dataLoader"
        paginated
        layout="fill"
        entity-type="paymentRequestPosition"
        selectable
        :selected-rows="selectedOrderPositions"
        :disable-row-when="isRowDisabled"
        @row:selected="onRowSelected"
        @row:deselected="onRowDeselected"
    >
      <template #empty>
        <div class="no-data">
          Нет позиций
        </div>
      </template>

      <VDataGridColumnV2
          id="id"
          width="100px"
      >
        <template #label>
          <div>Позиция</div>
          <div class="description">
            Заказ №
          </div>
        </template>

        <template #default="{ row }">
          <VLink @click="OrdersService.showOrderPositionCard(row)">
            {{ $core.utils.string.formatWithDelimiter(row.id) }}
          </VLink>
          <div class="description">
            {{ $core.utils.string.formatWithDelimiter(row.order.id) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="name"
          sortable
          content-class="ellipsis"
      >
        <template #label>
          <div>Товар</div>
          <div class="description">
            Производитель / Материал
          </div>
        </template>

        <template #default="{ row }">
          <div
              class="ellipsis"
              :title="row.name"
          >
            {{ row.name }}
          </div>
          <div class="description">
            <span>{{ row.productCategory?.id }} / {{ row.buyersCode || '--' }}</span>

            <VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="price"
          content-align="right"
          width="120px"
      >
        <template #label>
          <div>Сумма</div>
          <div class="description">
            Цена
          </div>
        </template>

        <template #default="{ row }">
          <div class="nowrap">
            {{ row.sumFullSeller?.format(row.currencyCode) }}
          </div>
          <div class="description nowrap">
            {{ row.priceFullSeller?.format(row.currencyCode, row.unit) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="qty"
          label="Количество"
          width="180px"
      >
        <template #default="{ row }">
          <ShipmentStatusBar
              :shipment-status="row.shipmentStatus"
              :unit="row.unit"
              @open="(anchor) => OrdersService.showPositionShipmentsPopup(row, anchor)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="desiredReceiptDate"
          label="ЖДД"
          sortable
          width="100px"
      >
        <template #default="{ row }">
          <div class="nowrap">
            {{ $core.utils.date.humanizeDate(row.desiredReceiptDate, null) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
          id="sum"
          label="Сумма"
          width="180px"
      >
        <template #default="{ row }">
          <PaymentStatusBar
              :payment-status="row.paymentStatus"
              :currency="row.currencyCode"
              @open="(anchor) => OrdersService.showPositionPaymentsPopup(row, anchor)"
          />
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>
  </VDialogV3>
</template>

<style src="./PaymentRequestPositionsDialog.scss" lang="scss" />

<script lang="ts">

import DataGridLoader      from '@/lib/components/VDataGridV2/DataGridLoader';
import OrdersService       from '@/domains/Orders/OrdersService';
import PaymentStatusBar    from '@/domains/Orders/components/PaymentStatusBar/PaymentStatusBar.vue';
import ShipmentStatusBar   from '@/domains/Orders/components/ShipmentStatusBar/ShipmentStatusBar.vue';

import type OrderPosition  from '@/domains/Orders/OrderPosition';
import type PaymentRequest from '@/domains/PaymentRequests/PaymentRequest';

export default defineComponent({
  name: 'PaymentRequestPositionsDialog',

  components: {
    PaymentStatusBar,
    ShipmentStatusBar,
  },

  props: {
    paymentRequest: {
      type: Object as PropType<PaymentRequest>,
      required: true,
    },
    partnerId: {
      type: Number as PropType<number>,
      default: null,
    },
    companyId: {
      type: Number as PropType<number>,
      default: null,
    },
  },

  setup() {
    return {
      OrdersService,
    };
  },

  data() {
    const filters: Record<string, unknown> = {
      seller:        { id: this.partnerId },
      sellerCompany: { id: this.companyId },
      paymentStatus: 'no-request',
    };

    // Площадка попадает в фильтр, только если задана в документе:
    // явный null сузил бы выборку до позиций вообще без площадки.
    if (this.paymentRequest.productionSite) {
      filters.buyerProductionSite = this.paymentRequest.productionSite;
    }

    return {
      dataLoader: new DataGridLoader(OrdersService.getPositionsListPaginated).useExtraParams(filters),
    };
  },

  computed: {
    dialogTitle(): string {
      if (this.paymentRequest.productionSite) {
        return `Позиции без заявок на платёж по площадке "${this.paymentRequest.productionSite.name}"`;
      } else {
        return 'Позиции без заявок на платёж';
      }
    },

    /**
     * Выбранные строки — позиции заказов, уже добавленные в документ.
     * Источник истины сам paymentRequest.positions; строки грида —
     * OrderPosition, поэтому достаём из каждой позиции документа её orderPosition.
     */
    selectedOrderPositions(): OrderPosition[] {
      return this.paymentRequest.positions.map(position => position.orderPosition);
    },
  },

  methods: {
    /**
     * Дизейбл строки. Возвращает строку-причину (станет подписью поверх
     * disabled-строки) либо false, если позицию можно добавить в документ.
     * Позиция блокируется если не совпадает валюта, НДС, категория и т.д.
     */
    isRowDisabled(orderPosition: OrderPosition): string | false {
      const reasons: string[] = [];

      if (
          this.paymentRequest.orderCurrencyCode
          && orderPosition.currencyCode !== this.paymentRequest.orderCurrencyCode
      ) {
        reasons.push(`Валюта позиции (${orderPosition.currencyCode}) не соответствует валюте платежа (${this.paymentRequest.orderCurrencyCode})`);
      }

      if (
          this.$core.utils.isNullOrUndefined(this.paymentRequest.isVatIncluded) === false
          && orderPosition.isVatIncludedSeller !== this.paymentRequest.isVatIncluded
      ) {
        reasons.push(`Тип НДС ${orderPosition.isVatIncludedSeller ? '"Включительно"' : '"Сверху"'} не соответствует документу`);
      }

      if (
          this.paymentRequest.invoice?.currencyCode
          && orderPosition.currencyCode !== this.paymentRequest.invoice.currencyCode
          && this.paymentRequest.invoice.currencyCode !== this.paymentRequest.actualPaymentCurrencyCode
      ) {
        reasons.push(`Валюта счёта (${this.paymentRequest.invoice.currencyCode}) не соответствует валюте позиции (${orderPosition.currencyCode})`);
      }

      if (
          this.$core.utils.isNullOrUndefined(this.paymentRequest.isOfferBased) === false
          && orderPosition.isOfferBased !== this.paymentRequest.isOfferBased
      ) {
        reasons.push('Позиция по предложению, а платёж прямой — или наоборот');
      }

      if (
          this.$core.utils.isNullOrUndefined(this.paymentRequest.orderCategory) === false
          && orderPosition.orderCategory?.id !== this.paymentRequest.orderCategory?.id
      ) {
        reasons.push(`Категория заказа другая: в платеже «${this.paymentRequest.orderCategory?.name ?? this.paymentRequest.orderCategory?.id}»`);
      }

      if (orderPosition.paymentStatus.isFullyClassified) {
        reasons.push('Позиция полностью оплачена');
      }

      if (
          this.paymentRequest.buyerCompany?.id
          && this.paymentRequest.buyerCompany.id !== orderPosition.buyerCompany?.id
      ) {
        reasons.push('Плательщик в документе не соответствует покупателю в позиции');
      }

      if (
          this.paymentRequest.productionSite?.id
          && this.paymentRequest.productionSite.id !== orderPosition.buyerProductionSite?.id
      ) {
        reasons.push('Производственная площадка не соответствует позиции');
      }

      if (reasons.length > 0) {
        return reasons.join('. ');
      } else {
        return false;
      }
    },

    /** Колбэк выбора строки — добавляем позицию заказа в документ. */
    onRowSelected(orderPosition: OrderPosition): void {
      this.paymentRequest.addPosition(orderPosition);
    },

    /** Колбэк снятия строки — убираем позицию заказа из документа. */
    onRowDeselected(orderPosition: OrderPosition): void {
      this.paymentRequest.deletePosition(orderPosition);
    },

    onSubmit(): void {
      this.$core.dialogs.PaymentRequestPositionsDialog.hide();
    }
  },
});
</script>
